import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Plus, Trash2, Download, ChevronRight, Upload } from "lucide-react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import FileUpload from "@/components/FileUpload";

export default function AdminNotes() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [notes, setNotes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", file_url: "", chapter_id: "", description: "", pages: "" });

  useEffect(() => { fetchCourses(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCourses = async () => {
    const { data } = await api.get("/courses");
    setCourses(data);
  };

  const selectCourse = async (course) => {
    setSelectedCourse(course);
    setShowForm(false);
    try {
      const [notesRes, chaptersRes] = await Promise.all([
        api.get(`/courses/${course.id}/notes`),
        api.get(`/courses/${course.id}/chapters`).catch(() => ({ data: [] })),
      ]);
      setNotes(notesRes.data);
      setChapters(chaptersRes.data);
    } catch (err) {
      setNotes([]);
      setChapters([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCourse || !form.file_url) return;
    try {
      await api.post(`/courses/${selectedCourse.id}/notes`, form);
      setShowForm(false);
      setForm({ title: "", file_url: "", chapter_id: "", description: "", pages: "" });
      const { data } = await api.get(`/courses/${selectedCourse.id}/notes`);
      setNotes(data);
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    await api.delete(`/notes/${noteId}`);
    setNotes((prev) => prev.filter((n) => n.id !== noteId));
  };

  const inputClass = "w-full admin-input border-b-2 px-4 py-3 text-sm focus:outline-none focus:border-[#0284C7] transition-none";

  return (
    <AdminLayout>
      <div data-testid="admin-notes-page">
        <div className="mb-6">
          <h1 className="text-2xl font-black admin-text flex items-center gap-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            <FileText size={24} className="text-[#0284C7]" />
            PDF Notes Manager
          </h1>
          <p className="text-sm admin-text-secondary mt-0.5">Upload free PDF notes for each course. Students can download and print.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Course List */}
          <div className="admin-card border p-4">
            <h3 className="text-sm font-bold admin-text mb-3">Select Course</h3>
            <div className="space-y-1 max-h-[600px] overflow-y-auto">
              {courses.map((course) => (
                <button
                  key={course.id}
                  data-testid={`notes-course-${course.id}`}
                  onClick={() => selectCourse(course)}
                  className={`w-full flex items-center gap-3 p-3 text-left transition-none ${
                    selectedCourse?.id === course.id ? "bg-[#0284C7]/10 border-l-4 border-[#0284C7]" : "hover:bg-slate-50"
                  }`}
                >
                  <div className="w-10 aspect-video bg-slate-200 flex-shrink-0 overflow-hidden">
                    <img src={course.image_url || ""} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold admin-text truncate">{course.title}</p>
                    <p className="text-[10px] admin-text-secondary">{course.category}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Notes Management */}
          <div className="lg:col-span-2">
            {selectedCourse ? (
              <div className="admin-card border p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-base font-bold admin-text">{selectedCourse.title}</h3>
                    <p className="text-xs admin-text-secondary">{notes.length} notes uploaded</p>
                  </div>
                  <button
                    data-testid="add-note-btn"
                    onClick={() => { setShowForm(!showForm); setForm({ title: "", file_url: "", chapter_id: "", description: "", pages: "" }); }}
                    className="bg-[#0284C7] text-white font-bold py-2.5 px-4 text-xs flex items-center gap-1.5 transition-none"
                  >
                    <Plus size={14} /> Add PDF Note
                  </button>
                </div>

                {/* Upload Form */}
                {showForm && (
                  <form onSubmit={handleSubmit} className="admin-card border p-4 mb-4 space-y-3" data-testid="note-form">
                    <div>
                      <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Note Title</label>
                      <input
                        data-testid="note-title-input"
                        placeholder="e.g., Chapter 1 - Important Formulas"
                        value={form.title}
                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                        className={inputClass}
                        required
                      />
                    </div>

                    <FileUpload
                      label="Upload PDF File"
                      accept=".pdf,application/pdf"
                      uploadType="notes"
                      currentUrl={form.file_url}
                      onUpload={(url) => setForm({ ...form, file_url: url })}
                    />

                    {form.file_url && (
                      <div className="bg-green-50 p-2 text-xs text-green-700 flex items-center gap-2">
                        <FileText size={14} /> PDF uploaded successfully
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Chapter (Optional)</label>
                        <select value={form.chapter_id} onChange={(e) => setForm({ ...form, chapter_id: e.target.value })} className={inputClass}>
                          <option value="">General / All Chapters</option>
                          {chapters.map((ch) => (
                            <option key={ch.id} value={ch.id}>{ch.title}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Pages</label>
                        <input placeholder="e.g., 25 pages" value={form.pages} onChange={(e) => setForm({ ...form, pages: e.target.value })} className={inputClass} />
                      </div>
                    </div>

                    <div>
                      <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Description</label>
                      <input placeholder="Brief description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputClass} />
                    </div>

                    <div className="flex gap-2">
                      <button data-testid="note-submit-btn" type="submit" disabled={!form.file_url} className="bg-[#0284C7] text-white font-bold py-2.5 px-6 text-xs transition-none disabled:opacity-40">
                        Save Note
                      </button>
                      <button type="button" onClick={() => setShowForm(false)} className="admin-card border font-bold py-2.5 px-6 text-xs transition-none admin-text">
                        Cancel
                      </button>
                    </div>
                  </form>
                )}

                {/* Notes List */}
                <div className="space-y-2" data-testid="notes-list">
                  {notes.map((note) => {
                    const chapterName = chapters.find(c => c.id === note.chapter_id)?.title;
                    return (
                      <div key={note.id} className="admin-card border p-3 flex items-center gap-3">
                        <div className="w-10 h-10 bg-red-50 flex items-center justify-center flex-shrink-0">
                          <FileText size={18} className="text-red-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold admin-text truncate">{note.title}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            {chapterName && <span className="text-[10px] text-[#0284C7] font-medium">{chapterName}</span>}
                            {note.pages && <span className="text-[10px] admin-text-secondary">{note.pages}</span>}
                            {note.description && <span className="text-[10px] admin-text-secondary truncate">{note.description}</span>}
                          </div>
                        </div>
                        <a href={note.file_url} target="_blank" rel="noreferrer" className="p-1.5 text-[#0284C7]" title="Download">
                          <Download size={14} />
                        </a>
                        <button onClick={() => handleDelete(note.id)} className="p-1.5 text-red-400" title="Delete">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    );
                  })}
                  {notes.length === 0 && (
                    <div className="text-center py-8">
                      <FileText size={40} className="mx-auto text-slate-300 mb-3" />
                      <p className="text-xs admin-text-secondary">No PDF notes uploaded yet. Click "Add PDF Note" to upload.</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="admin-card border p-12 text-center">
                <FileText size={48} className="mx-auto admin-text-secondary mb-4 opacity-30" />
                <p className="admin-text-secondary text-sm">Select a course to manage its PDF notes</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
